import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { toast } from "sonner";
import { ArrowLeft, Save, RefreshCw, Mail, CheckCircle2 } from "lucide-react";

export default function AdminAgentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const { data } = await api.get(`/admin/agents/${id}`);
    setData(data);
    setForm({
      agent_name: data.user.agent_name || "",
      agency_name: data.user.agency_name || "",
      email: data.user.email || "",
      mobile: data.user.mobile || "",
      status: data.user.status || "",
    });
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    await api.put(`/admin/agents/${id}`, form);
    toast.success("Updated");
    load();
  };

  const renew = async () => {
    if (!window.confirm("Renew this agent's subscription by 365 days?")) return;
    await api.post(`/admin/agents/${id}/renew`, { duration_days: 365 });
    toast.success("Renewed — confirmation email sent");
    load();
  };

  const sendReminder = async () => {
    try {
      const { data } = await api.post(`/admin/agents/${id}/send-reminder`);
      toast.success(data.sent ? `Reminder sent (${data.days_left} days left)` : "Email skipped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
  };

  const activateOffline = async () => {
    const plan = data?.user?.requested_plan_name || data?.user?.requested_plan || "default plan";
    if (!window.confirm(`Activate this agent on ${plan}?\nThis records the offline payment, sets subscription expiry, and emails the agent.`)) return;
    try {
      await api.post(`/admin/agents/${id}/activate-offline`, {});
      toast.success("Activated — confirmation email sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    }
  };

  if (!data) return <div className="p-10">Loading…</div>;

  return (
    <div className="flex min-h-screen">
      <Sidebar role="admin" />
      <main className="flex-1 bg-[#FCFCFC] p-8 lg:p-12">
        <div className="max-w-4xl">
          <Link to="/admin/agents" data-testid="back-to-agents" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 hover:text-[#16A34A]"><ArrowLeft size={13}/> All agents</Link>
          <div className="mt-4 flex items-start justify-between">
            <div>
              <div className="overline">Agent Profile</div>
              <h1 className="font-editorial text-4xl mt-2">{data.user.agent_name}</h1>
              <div className="text-sm text-slate-500 mt-1 font-mono-e">{data.user.agent_id || "Not yet activated"}</div>
            </div>
            <button data-testid="admin-agent-save" onClick={save} className="btn-bronze inline-flex items-center gap-2"><Save size={14}/> Save</button>
          </div>

          <div className="mt-4 flex gap-2">
            {(data.user.status === "pending_activation" || data.user.status === "pending_payment") && (
              <button data-testid="admin-agent-activate-offline" onClick={activateOffline} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3.5 py-2 bg-[#16A34A] text-white rounded hover:bg-emerald-700"><CheckCircle2 size={13}/> Approve &amp; Activate</button>
            )}
            <button data-testid="admin-agent-renew" onClick={renew} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3.5 py-2 border border-[#16A34A] text-[#16A34A] rounded hover:bg-[#16A34A] hover:text-white transition-colors"><RefreshCw size={13}/> Renew +365 days</button>
            <button data-testid="admin-agent-remind" onClick={sendReminder} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3.5 py-2 border border-slate-300 rounded hover:border-[#16A34A]"><Mail size={13}/> Send expiry reminder</button>
          </div>

          {(data.user.status === "pending_activation" || data.user.status === "pending_payment") && (
            <div data-testid="pending-activation-card" className="mt-6 bg-amber-50 border border-amber-200 rounded p-5">
              <div className="overline text-amber-800">Awaiting activation</div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm text-slate-700">
                <div><span className="text-slate-500">Requested plan:</span> <b>{data.user.requested_plan_name || data.user.requested_plan || "—"}</b></div>
                {data.user.requested_plan_price ? (
                  <div><span className="text-slate-500">Amount:</span> <b>₹{Number(data.user.requested_plan_price).toLocaleString()}</b></div>
                ) : null}
                <div><span className="text-slate-500">Requested at:</span> {data.user.activation_requested_at ? new Date(data.user.activation_requested_at).toLocaleString() : "—"}</div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-3 mt-8">
            <Stat label="Status" value={data.user.status} />
            <Stat label="Quotations" value={data.quotations_count} />
            <Stat label="Expiry" value={data.user.subscription_expiry ? new Date(data.user.subscription_expiry).toLocaleDateString() : "—"} />
          </div>

          <div className="mt-8 bg-white border border-border rounded p-6 grid grid-cols-2 gap-4">
            <F label="Agent Name" value={form.agent_name} onChange={(v)=>setForm({...form, agent_name:v})} testid="agent-edit-name" />
            <F label="Agency Name" value={form.agency_name} onChange={(v)=>setForm({...form, agency_name:v})} testid="agent-edit-agency" />
            <F label="Email" value={form.email} onChange={(v)=>setForm({...form, email:v})} testid="agent-edit-email" />
            <F label="Mobile" value={form.mobile} onChange={(v)=>setForm({...form, mobile:v})} testid="agent-edit-mobile" />
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Status</label>
              <select data-testid="agent-edit-status" value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})} className="w-full border border-border rounded px-3 py-2 text-sm bg-white">
                <option value="pending_payment">Pending Payment</option>
                <option value="pending_activation">Pending Activation</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
              <button
                data-testid="admin-reset-password-btn"
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Reset password for ${form.agent_name || form.email}? A temporary password will be emailed to the agent and they'll be forced to change it on next login.`)) return;
                  try {
                    const r = await api.post(`/admin/agents/${data.user.id}/reset-password`);
                    toast.success(r.data?.message || "Reset initiated");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Reset failed");
                  }
                }}
                className="w-full border border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100 py-2 rounded text-sm">
                Reset Password
              </button>
              <div className="text-[10px] text-slate-400 mt-1">Logged in Admin → Password Audit</div>
            </div>
          </div>

          {data.profile && (
            <div className="mt-6 bg-white border border-border rounded p-6">
              <div className="overline mb-3">Business Profile</div>
              <div className="text-sm grid grid-cols-2 gap-4">
                <div><div className="text-xs text-slate-500">Address</div><div>{data.profile.address || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Website</div><div>{data.profile.website || "—"}</div></div>
                <div><div className="text-xs text-slate-500">GST</div><div>{data.profile.gst_number || "—"}</div></div>
                <div><div className="text-xs text-slate-500">PAN</div><div>{data.profile.pan || "—"}</div></div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-border rounded p-5">
      <div className="overline">{label}</div>
      <div className="mt-2 text-2xl font-editorial">{value}</div>
    </div>
  );
}
function F({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <input data-testid={testid} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
    </div>
  );
}
